import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";

/**
 * Produce a cropped image Blob from a source URL + pixel crop area.
 * Keeps the cropped region at its natural resolution (no downscaling), so
 * free-form "detail" photos stay full-res. PNG for square logos/headshots
 * (preserves transparency), JPEG for larger free-form photos.
 */
async function getCroppedBlob(imageSrc, cropPixels, mime) {
    const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = imageSrc;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(cropPixels.width);
    canvas.height = Math.round(cropPixels.height);
    const ctx = canvas.getContext("2d");
    // Draw the whole image offset by the crop origin. When zoomed IN the crop rect
    // sits inside the image (normal crop); when zoomed OUT below fit, the crop rect is
    // larger than the image, so it renders centered with transparent padding — letting
    // a logo be smaller than the square it fills.
    ctx.drawImage(image, -cropPixels.x, -cropPixels.y, image.naturalWidth, image.naturalHeight);
    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), mime, mime === "image/jpeg" ? 0.92 : undefined);
    });
}

/**
 * Full-screen crop modal.
 * Props: file (File), aspect (number|null for free-form), title, onCancel, onComplete(blob).
 */
export default function ImageCropper({ file, aspect = null, round = false, title = "Adjust image", onCancel, onComplete }) {
    const [src, setSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [pixels, setPixels] = useState(null);
    const [working, setWorking] = useState(false);
    // Square logos / round headshots are saved as transparent PNG, so we let the user
    // zoom BELOW fit to make the artwork smaller than the frame. The floor is derived
    // from the image's own aspect ratio so even very wide/tall (irregular) logos can be
    // shrunk until they fully fit inside the square, with a little padding. Free-form
    // JPEG photos have no transparency, so they stay locked to fill (min 1×).
    const isSquareish = aspect === 1 || round;
    const [minZoom, setMinZoom] = useState(isSquareish ? 0.5 : 1);

    const onMediaLoaded = useCallback((media) => {
        if (!isSquareish) return;
        const shortSide = Math.min(media.naturalWidth, media.naturalHeight);
        const longSide = Math.max(media.naturalWidth, media.naturalHeight);
        // shortSide/longSide == the zoom at which the WHOLE image just fits; go a touch
        // lower for padding, and never let a near-square logo lose its usual 0.5× range.
        const containZoom = shortSide / longSide;
        setMinZoom(Math.max(0.05, Math.min(0.5, containZoom * 0.9)));
    }, [isSquareish]);

    useEffect(() => {
        if (!file) return undefined;
        const url = URL.createObjectURL(file);
        setSrc(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

    const apply = async () => {
        if (!pixels) return;
        setWorking(true);
        try {
            const mime = aspect === 1 ? "image/png" : "image/jpeg";
            const blob = await getCroppedBlob(src, pixels, mime);
            onComplete(blob);
        } finally {
            setWorking(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[2000] bg-black/80 flex items-center justify-center p-4" data-testid="image-cropper">
            <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a]">
                <div className="px-4 py-3 border-b border-[#2a2a2a] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                    {title} {round ? "· circle" : aspect === 1 ? "· square" : "· free-form"}
                </div>
                <div className="relative w-full" style={{ height: 340, background: "#0a0a0a" }}>
                    {src && (
                        <Cropper
                            image={src}
                            crop={crop}
                            zoom={zoom}
                            minZoom={minZoom}
                            aspect={aspect || undefined}
                            restrictPosition={false}
                            showGrid={aspect !== 1 && !round}
                            cropShape={round ? "round" : "rect"}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                            onMediaLoaded={onMediaLoaded}
                        />
                    )}
                </div>
                <div className="px-4 py-3 flex items-center gap-3 border-t border-[#2a2a2a]">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Zoom</span>
                    <input
                        type="range" min={minZoom} max={4} step={0.05} value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        data-testid="cropper-zoom"
                        className="flex-1 accent-[#ff4500]"
                    />
                </div>
                <div className="px-4 py-3 flex justify-end gap-2 border-t border-[#2a2a2a]">
                    <button type="button" onClick={onCancel} className="h-9 px-3 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white">Cancel</button>
                    <Button type="button" onClick={apply} disabled={working || !pixels} data-testid="cropper-apply" className="h-9 px-4 rounded-none text-[10px] uppercase tracking-[0.2em] bg-[#ff4500] hover:bg-[#ff5722] font-semibold">
                        {working ? "Applying…" : "Apply & Upload"}
                    </Button>
                </div>
                <div className="px-4 pb-3 text-[10px] text-[#666]">Drag to move · scroll or use the slider to zoom{round ? " · circular crop · zoom out to shrink the artwork" : aspect === 1 ? " · locked to a square · zoom out to make the logo smaller than the frame" : " · free-form; the detail page shows full resolution"}</div>
            </div>
        </div>
    );
}
